import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Checkbox, FormControlLabel,
  Divider, List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, Alert, Paper, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Tooltip, TextField, Autocomplete, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as ProcessIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import DiscReportModal from '../../disc/DiscReportModal';
import ReasoningTestResultModal from '../../reasoningTests/ReasoningTestResultModal';
import PersonalHabilitiesTestResultModal from '../../personalHabilitiesTests/PersonalHabilitiesTestResultModal';
import SalesAssessmentReportModal from '../../salesAssessment/SalesAssessmentReportModal';

const COLLECTION_MAP = {
  disc_test:        'disc_tests',
  reasoning_test:   'reasoning_tests',
  personal_skills:  'reasoning_tests',
  sales_assessment: 'sales_assessments',
};

const REASONING_TYPES = [
  { id: 'wordAssociation',    labelKey: 'reasoningTests.wordAssociation'    },
  { id: 'logicalProcess',     labelKey: 'reasoningTests.logicalProcess'     },
  { id: 'numericalReasoning', labelKey: 'reasoningTests.numericalReasoning' },
  { id: 'socialContext',      labelKey: 'reasoningTests.socialContext'      },
  { id: 'logicalAbstract',    labelKey: 'reasoningTests.logicalAbstract'    },
];

const PERSONAL_SKILLS_TYPES = [
  { id: 'integrityTest',        labelKey: 'personalHabilitiesTests.integrityTest'        },
  { id: 'emotionalIntelligence',labelKey: 'personalHabilitiesTests.emotionalIntelligence' },
];

const MODULE_FETCH = {
  disc_test: {
    fetchUrl: (project, positionId) =>
      `/disc-tests/by-position?positionId=${positionId}&projectId=${project}`,
    processUrl: (testId) => `/disc-tests/${testId}/process`,
    testTypes: null,
    filterTypes: null,
  },
  reasoning_test: {
    fetchUrl: (project, positionId) =>
      `/reasoning-tests/by-position?positionId=${positionId}&projectId=${project}`,
    processUrl: (testId, project) => `/reasoning-tests/${testId}/process?projectId=${project}`,
    testTypes: REASONING_TYPES,
    filterTypes: null,
  },
  personal_skills: {
    fetchUrl: (project, positionId) =>
      `/reasoning-tests/by-position?positionId=${positionId}&projectId=${project}`,
    processUrl: (testId, project) => `/reasoning-tests/${testId}/process?projectId=${project}`,
    testTypes: PERSONAL_SKILLS_TYPES,
    filterTypes: ['integrityTest', 'emotionalIntelligence'],
  },
  sales_assessment: {
    fetchUrl: (project, positionId) =>
      `/sales-assessments/by-position?positionId=${positionId}&projectId=${project}`,
    processUrl: (testId) => `/sales-assessments/${testId}/process`,
    testTypes: null,
    filterTypes: null,
  },
};

const STATUS_CONFIG = {
  pending:          { labelKey: 'ats.testStatus.pending',    color: 'default'  },
  sent:             { labelKey: 'ats.testStatus.sent',       color: 'info'     },
  started:          { labelKey: 'ats.testStatus.started',    color: 'info'     },
  completed:        { labelKey: 'ats.testStatus.completed',  color: 'warning'  },
  processing:       { labelKey: 'ats.testStatus.processing', color: 'warning'  },
  evaluated:        { labelKey: 'ats.testStatus.evaluated',  color: 'success'  },
  processing_error: { labelKey: 'ats.testStatus.error',      color: 'error'    },
  expired:          { labelKey: 'ats.testStatus.expired',    color: 'error'    },
  invalid:          { labelKey: 'ats.testStatus.invalid',    color: 'error'    },
};

const AtsTestSendDialog = ({
  open,
  onClose,
  moduleId,
  moduleConfig,
  applications,
  positionId,
  onSendBulk,
  sending,
  sendResults,
  // Apertura directa en el formulario de envío con candidatos preseleccionados
  // (usado por el drawer del candidato). Por defecto se mantiene el
  // comportamiento del pipeline: abrir en la lista sin selección.
  initialView = 'list',
  initialSelectedIds = null,
  // Competencias predeterminadas de la vacante (positions.competencias,
  // [{ competencyId, competencyName }]): el envío DISC parte con ellas.
  positionCompetencies = null,
}) => {
  const { t } = useTranslation();
  const { project, organization, language, authToken, showAlert } = useStore.getState();

  const [view, setView] = useState('list');
  const [tests, setTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [processing, setProcessing] = useState({});
  const [resultTest, setResultTest] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);

  // Configuración DISC (solo moduleId === 'disc_test'): modo simple/completo.
  // En completo NO se selecciona cargo: se usa siempre el de la vacante actual.
  const [discMode, setDiscMode] = useState('complete');
  const [discCurrentPosition, setDiscCurrentPosition] = useState('');
  const [discCompetencies, setDiscCompetencies] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [loadingCompetencies, setLoadingCompetencies] = useState(false);

  // Selección inicial de competencias del DISC: las predeterminadas de la
  // vacante, igual que en Evaluaciones. Se resuelven contra el catálogo si ya
  // está cargado; si no, mismo objeto { id, name } del Autocomplete (el envío
  // solo lee id y nombre). Agregar o quitar después aplica solo a este envío:
  // nunca modifica la vacante. Sin predeterminadas queda vacía, como antes.
  const buildDefaultDiscCompetencies = () => {
    const defaults = Array.isArray(positionCompetencies) ? positionCompetencies : [];
    const seen = new Set();
    return defaults
      .map((c) => {
        if (!c || typeof c !== 'object') return null;
        const id = c.competencyId ?? c.id ?? c.uid;
        if (!id || seen.has(id)) return null;
        seen.add(id);
        const fromCatalog = competencies.find((opt) => (opt.id ?? opt.uid) === id);
        return fromCatalog || { id, name: c.competencyName ?? c.name ?? '' };
      })
      .filter(Boolean);
  };

  const resetDiscConfig = () => {
    setDiscMode('complete');
    setDiscCurrentPosition('');
    setDiscCompetencies(buildDefaultDiscCompetencies());
  };

  useEffect(() => {
    if (open && moduleId) {
      const startInCreate = initialView === 'create';
      setView(startInCreate ? 'create' : 'list');
      setSelectedCandidates(
        startInCreate && Array.isArray(initialSelectedIds) ? initialSelectedIds : []
      );
      setSelectedTypes([]);
      resetDiscConfig();
      loadTests();
    }
  }, [open, moduleId]);

  // Cargar competencias (lazy) solo cuando se configura un envío DISC en modo completo
  useEffect(() => {
    const loadCompetencies = async () => {
      if (!authToken || !organization) return;
      try {
        setLoadingCompetencies(true);
        const response = await api.get(`/capabilities`, {
          params: { language, organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.data.errorCode === 0) {
          setCompetencies(response.data.response?.data || []);
        }
      } catch (err) {
        console.error('Error loading competencies:', err);
      } finally {
        setLoadingCompetencies(false);
      }
    };

    if (
      open &&
      view === 'create' &&
      moduleId === 'disc_test' &&
      discMode === 'complete' &&
      competencies.length === 0 &&
      !loadingCompetencies
    ) {
      loadCompetencies();
    }
  }, [open, view, moduleId, discMode]);

  const loadTests = async () => {
    if (!moduleId || !MODULE_FETCH[moduleId]) return;
    setLoadingTests(true);
    try {
      const url = MODULE_FETCH[moduleId].fetchUrl(project, positionId);
      const response = await api.get(url, { headers: { Authorization: `Bearer ${authToken}` } });
      let data = response.data.response?.data || [];

      const filterTypes = MODULE_FETCH[moduleId].filterTypes;
      if (filterTypes) {
        data = data.filter((t) => filterTypes.includes(t.testType));
      } else if (moduleId === 'reasoning_test') {
        data = data.filter((t) => !['integrityTest', 'emotionalIntelligence'].includes(t.testType));
      }

      setTests(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTests(false);
    }
  };

  const handleProcess = async (testId) => {
    if (!moduleId || !MODULE_FETCH[moduleId]) return;
    setProcessing((prev) => ({ ...prev, [testId]: true }));
    try {
      const url = MODULE_FETCH[moduleId].processUrl(testId, project);
      await api.post(url, {}, { headers: { Authorization: `Bearer ${authToken}` } });
      showAlert(t('ats.drawer.testProcessing'), 'success');

      setTests((prev) =>
        prev.map((t) => (t.id || t.uid) === testId ? { ...t, status: 'processing' } : t)
      );

      const collectionName = COLLECTION_MAP[moduleId];
      const testRef = doc(db, collectionName, testId);
      const unsubscribe = onSnapshot(testRef, (docSnap) => {
        if (!docSnap.exists()) return;
        const data = docSnap.data();
        if (data.status !== 'processing') {
          unsubscribe();
          setTests((prev) =>
            prev.map((t) => (t.id || t.uid) === testId ? { ...t, ...data, id: testId } : t)
          );
          setProcessing((prev) => ({ ...prev, [testId]: false }));
        }
      });
    } catch (err) {
      console.error(err);
      showAlert(t('ats.drawer.testProcessError'), 'error');
      setProcessing((prev) => ({ ...prev, [testId]: false }));
    }
  };

  const handleSend = async () => {
    const discOptions = moduleId === 'disc_test'
      ? {
          mode: discMode,
          currentPosition: discCurrentPosition.trim(),
          competencies: discCompetencies.map((c) => ({
            competencyId: c.id ?? c.uid,
            competencyName: c.name ?? c.capabilityName ?? '',
          })),
        }
      : null;

    await onSendBulk(selectedCandidates, selectedTypes.length > 0 ? selectedTypes : null, discOptions);
    await loadTests();
    setView('list');
    setSelectedCandidates([]);
    setSelectedTypes([]);
    resetDiscConfig();
  };

  if (!moduleConfig) return null;

  const fetchConfig = MODULE_FETCH[moduleId];

  const getStatusChip = (status) => {
    const conf = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return <Chip label={t(conf.labelKey)} color={conf.color} size="small" />;
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('es-CL');
    if (ts.toDate) return ts.toDate().toLocaleDateString('es-CL');
    return new Date(ts).toLocaleDateString('es-CL');
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => { if (!sending) onClose(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {view === 'create' && (
              <IconButton size="small" onClick={() => setView('list')} disabled={sending}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant="h6">
              {view === 'list' ? t(moduleConfig.labelKey) : t('ats.testDialog.newSend')}
            </Typography>
          </Box>
          {view === 'list' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { setSelectedCandidates([]); setSelectedTypes([]); resetDiscConfig(); setView('create'); }}
              sx={{ backgroundColor: moduleConfig.color, '&:hover': { filter: 'brightness(0.9)' } }}
            >
              {t('ats.testDialog.sendNew')}
            </Button>
          )}
        </DialogTitle>

        <DialogContent sx={{ minHeight: 300 }}>

          {/* LISTA */}
          {view === 'list' && (
            <>
              {loadingTests ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : tests.length === 0 ? (
                <Alert severity="info">{t('ats.testDialog.noTestsSent')}</Alert>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                        <TableCell sx={{ fontWeight: 600 }}>{t('ats.detail.candidate')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('cvFiltering.tableStatus')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('cvFiltering.tableDate')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tests.map((test) => {
                        const testId = test.id || test.uid;
                        const isProcessing = processing[testId];
                        return (
                          <TableRow key={testId} hover>
                            <TableCell>
                              <Typography variant="caption" fontWeight={600}>
                                {test.applicantName || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {test.applicantEmail}
                              </Typography>
                              {test.testType && (
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                  {t(`reasoningTests.${test.testType}`) !== `reasoningTests.${test.testType}`
                                    ? t(`reasoningTests.${test.testType}`)
                                    : t(`personalHabilitiesTests.${test.testType}`) !== `personalHabilitiesTests.${test.testType}`
                                      ? t(`personalHabilitiesTests.${test.testType}`)
                                      : test.testType
                                  }
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{getStatusChip(test.status)}</TableCell>
                            <TableCell>
                              <Typography variant="caption">{formatDate(test.createdAt)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              {(test.status === 'completed' || test.status === 'processing_error') && (
                                <Tooltip title={t('ats.drawer.processTest')}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleProcess(testId)}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing ? <CircularProgress size={16} /> : <ProcessIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {test.status === 'processing' && (
                                <CircularProgress size={18} sx={{ color: moduleConfig.color }} />
                              )}
                              {test.status === 'evaluated' && (
                                <Tooltip title={t('ats.drawer.viewResult')}>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => { setResultTest(test); setResultModalOpen(true); }}
                                  >
                                    <ViewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* CREAR */}
          {view === 'create' && !sending && !sendResults && (
            <>
              {/* Configuración DISC: modo simple/completo. El cargo no se
                  selecciona porque se usa el de la vacante actual. */}
              {moduleId === 'disc_test' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('disc.testMode')}
                  </Typography>
                  <ToggleButtonGroup
                    value={discMode}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) setDiscMode(val); }}
                  >
                    <ToggleButton value="simple">{t('disc.simpleMode')}</ToggleButton>
                    <ToggleButton value="complete">{t('disc.completeMode')}</ToggleButton>
                  </ToggleButtonGroup>

                  {discMode === 'complete' && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t('ats.testDialog.discPositionInfo')}
                      </Alert>

                      <TextField
                        fullWidth
                        size="small"
                        label={`${t('disc.currentPosition')} ${t('disc.optional')}`}
                        placeholder={t('disc.currentPositionPlaceholder')}
                        value={discCurrentPosition}
                        onChange={(e) => setDiscCurrentPosition(e.target.value)}
                        sx={{ mb: 2 }}
                      />

                      <Autocomplete
                        multiple
                        size="small"
                        options={competencies}
                        getOptionLabel={(option) => option.name || option.capabilityName || ''}
                        isOptionEqualToValue={(option, value) =>
                          (option.id ?? option.uid) === (value.id ?? value.uid)
                        }
                        value={discCompetencies}
                        onChange={(_, newValue) => setDiscCompetencies(newValue)}
                        loading={loadingCompetencies}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.id ?? option.uid ?? index}
                              label={option.name || option.capabilityName}
                              size="small"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`${t('disc.selectCompetencies')} ${t('disc.optional')}`}
                            helperText={t('disc.competenciesDescription')}
                          />
                        )}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {fetchConfig?.testTypes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('ats.testDialog.selectTypes')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {fetchConfig.testTypes.map((type) => {
                      const isSelected = selectedTypes.includes(type.id);
                      return (
                        <Chip
                          key={type.id}
                          label={t(type.labelKey)}
                          onClick={() => setSelectedTypes((prev) =>
                            isSelected ? prev.filter((id) => id !== type.id) : [...prev, type.id]
                          )}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: isSelected ? moduleConfig.color : undefined,
                            color: isSelected ? '#fff' : undefined,
                            borderColor: isSelected ? moduleConfig.color : undefined,
                            '&:hover': { opacity: 0.85 },
                          }}
                        />
                      );
                    })}
                  </Box>
                  {selectedTypes.length === 0 && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {t('ats.testDialog.selectAtLeastOneType')}
                    </Typography>
                  )}
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('ats.testDialog.subtitle')}
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCandidates.length === applications.length && applications.length > 0}
                    indeterminate={selectedCandidates.length > 0 && selectedCandidates.length < applications.length}
                    onChange={(e) => setSelectedCandidates(
                      e.target.checked ? applications.map((a) => a.uid || a.id) : []
                    )}
                  />
                }
                label={<Typography variant="body2" fontWeight={600}>{t('ats.cvFilter.selectAll')}</Typography>}
              />

              <Divider sx={{ my: 1 }} />

              <List dense>
                {applications.map((app) => {
                  const appId = app.uid || app.id;
                  const hasApplicantId = !!app.applicantId;
                  return (
                    <ListItem key={appId} disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          size="small"
                          checked={selectedCandidates.includes(appId)}
                          onChange={() => setSelectedCandidates((prev) =>
                            prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId]
                          )}
                          disabled={!hasApplicantId}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color={hasApplicantId ? 'inherit' : 'text.disabled'}>
                            {app.candidateName || '—'}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {app.candidateEmail}
                            {!hasApplicantId && ` — ${t('ats.cvFilter.noApplicantId')}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          {/* Enviando */}
          {sending && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t('ats.testDialog.sending')}
              </Typography>
            </Box>
          )}

          {/* Resultados del envío */}
          {!sending && sendResults && (
            <Box sx={{ py: 2 }}>
              {sendResults.sent > 0 && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {t('ats.testDialog.sent', { count: sendResults.sent })}
                </Alert>
              )}
              {sendResults.failed > 0 && (
                <Alert severity="warning">
                  {t('ats.testDialog.failed', { count: sendResults.failed })}
                </Alert>
              )}
            </Box>
          )}

        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={sending}>
            {t('close')}
          </Button>
          {view === 'create' && !sending && !sendResults && (
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={
                selectedCandidates.length === 0 ||
                (fetchConfig?.testTypes && selectedTypes.length === 0)
              }
              sx={{ backgroundColor: moduleConfig.color, '&:hover': { filter: 'brightness(0.9)' } }}
            >
              {t('ats.testDialog.send', { count: selectedCandidates.length })}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modales de resultado */}
      {moduleId === 'disc_test' && (
        <DiscReportModal
          open={resultModalOpen}
          onClose={() => { setResultModalOpen(false); setResultTest(null); }}
          test={resultTest}
        />
      )}
      {moduleId === 'reasoning_test' && (
        <ReasoningTestResultModal
          open={resultModalOpen}
          onClose={() => { setResultModalOpen(false); setResultTest(null); }}
          test={resultTest}
        />
      )}
      {moduleId === 'personal_skills' && (
        <PersonalHabilitiesTestResultModal
          open={resultModalOpen}
          onClose={() => { setResultModalOpen(false); setResultTest(null); }}
          test={resultTest}
        />
      )}
      {moduleId === 'sales_assessment' && (
        <SalesAssessmentReportModal
          open={resultModalOpen}
          onClose={() => { setResultModalOpen(false); setResultTest(null); }}
          test={resultTest}
        />
      )}
    </>
  );
};

export default AtsTestSendDialog;